import React, { useState } from "react";
import { Link, useSearchParams, Navigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Truck, Wrench, Hammer, HardHat, Zap, ShieldCheck,
  AlertTriangle, Search, Plus, Laptop, Gauge, Radio, AlignJustify,
} from "lucide-react";
import EquipmentRegisterTab, { type EquipmentTabKey } from "./EquipmentRegisterTab";
import LadderRegisterTab from "./LadderRegisterTab";
import { supabase } from "@/lib/supabase";
import { useStickyState } from "@/lib/stickyState";   // 29 Oct 2026 — filters survive back / refresh / rotate
import { useRequireAuth, hasRole } from "@/lib/auth";
import { Card, Pill, Button, Spinner, EmptyState } from "@/components/ui";
import { fmtDate, daysFromNow, urgencyFromDays } from "@/lib/dates";
import { isPresenting, MONEY_MASK } from "@/lib/presenting";

// =============================================================================
// Assets — unified tab navigation across asset types
//
// T-046 Part B:
//   - Fixed column names across the SELECTs/renderRows — the file was
//     querying fields that don't exist in the current schema (registration_
//     number, asset_code, wll_kg, last_test_date, item_description etc.)
//     causing PostgREST to return errors that rendered as empty lists.
//     All tabs now use the actual column names per the current schema.
//   - Added an "Assigned to" employee picker on vehicles / power tools /
//     rigging tabs. Manager+ can reassign inline via native select dropdown;
//     other roles see read-only. Triggers a row update + cache invalidate.
//
// 8 Oct 2026 (mig 429) — James: "Radios don't really have a place, nor do our
// test equipment or laptops and mobile phones ... a page with just a bunch of
// tables is okay but we can do better." Three new lists (IT & phones, Test
// equipment, Radios & other) read the equipment register that AP feeds; they
// live in EquipmentRegisterTab. The tab bar is now grouped — what we drive
// and use, the equipment, then the registers — so thirteen things in a row
// read as three.
//
// 8 Oct 2026 (mig 430) — Ladders: the register and its inspections, open to
// the crew to inspect. Lives in LadderRegisterTab.
//
// 22 Oct 2026 (mig 444) — James, decision 1: rigging, power tools and ladders
// move out of here to HR → Equipment (/hr/equipment); vehicles, hand tools,
// IT / test / radios and insurance stay. The old tabs are hidden from the
// strip and ?tab= links to them bounce to the new screen. The ladders tab
// stays reachable by ?tab=ladders only because the ladder create/edit form
// lives in LadderRegisterTab — the HR screen's "+ Ladder" points here.
// =============================================================================

const MOVED_TO_HR_EQUIPMENT = new Set<string>(["power_tools", "rigging", "test_and_tag", "rigging_inspections"]);
const HIDDEN_FROM_STRIP     = new Set<string>([...MOVED_TO_HR_EQUIPMENT, "ladders"]);

const TABS = [
  { key: "vehicles",            label: "Vehicles",          icon: Truck,       group: "Fleet & tools" },
  { key: "power_tools",         label: "Power tools",       icon: Wrench,      group: "Fleet & tools" },
  { key: "hand_tools",          label: "Hand tools",        icon: Hammer,      group: "Fleet & tools" },
  { key: "rigging",             label: "Rigging",           icon: HardHat,     group: "Fleet & tools" },
  { key: "ladders",             label: "Ladders",           icon: AlignJustify, group: "Fleet & tools" },
  { key: "eq_it",               label: "IT & phones",       icon: Laptop,      group: "Equipment" },
  { key: "eq_test",             label: "Test equipment",    icon: Gauge,       group: "Equipment" },
  { key: "eq_other",            label: "Radios & other",    icon: Radio,       group: "Equipment" },
  { key: "test_and_tag",        label: "T&T register",      icon: Zap,         group: "Registers" },
  { key: "rigging_inspections", label: "Rigging register",  icon: ShieldCheck, group: "Registers" },
  { key: "insurance_policies",  label: "Insurance",         icon: ShieldCheck, group: "Registers" },
] as const;
const TAB_GROUPS = ["Fleet & tools", "Equipment", "Registers"] as const;

type AnyTabKey = typeof TABS[number]["key"];
type TabKey = Exclude<AnyTabKey, EquipmentTabKey | "ladders">;
const isEquipmentTab = (k: AnyTabKey): k is EquipmentTabKey => k === "eq_it" || k === "eq_test" || k === "eq_other";
type AssignableType = "vehicle" | "power_tool" | "rigging";

interface EmployeeOption {
  employee_id: string;
  first_name:  string | null;
  last_name:   string | null;
}

export default function AssetsScreen() {
  const user = useRequireAuth();
  // ?tab=ladders — the Services due queue links straight to a tab (mig 431)
  const [params] = useSearchParams();
  const wanted = params.get("tab");
  const [tab, setTab] = useStickyState<AnyTabKey>("assets:tab", "vehicles", TABS.some(t => t.key === wanted) ? (wanted as AnyTabKey) : undefined);
  const [search, setSearch] = useStickyState("assets:search", "");

  const canEdit = hasRole(user, "manager");

  // mig 444 — these registers live on HR → Equipment now
  if (wanted && MOVED_TO_HR_EQUIPMENT.has(wanted)) {
    const kind = wanted === "power_tools" || wanted === "test_and_tag" ? "power_tool" : "rigging";
    return <Navigate to={`/hr/equipment?kind=${kind}`} replace />;
  }

  // Employees list for the assignment picker — shared across all assignable tabs
  const { data: employees = [] } = useQuery({
    queryKey: ["assets_assignable_employees"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("v_employees")
        .select("employee_id, first_name, last_name")
        .eq("is_active", true)
        .order("first_name");
      if (error) throw error;
      return (data ?? []) as unknown as EmployeeOption[];
    },
  });

  return (
    <div className="p-4 md:p-6 max-w-[1200px] mx-auto">
      <div className="mb-5 flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Assets</h1>
          <div className="text-text-muted text-[13px] mt-1">
            Everything Coretel owns, tracked with expiry dates and assignments.
            {canEdit && " You can allocate vehicles, tools, and rigging to employees inline."}
          </div>
        </div>
        {/* Mig 357 — the depot-rotation queue: everything with a due date
            (rigging inspections, T&T, service/rego/CTP) in one list. */}
        {canEdit && (
          <Link to="/assets/services" className="text-accent text-[13px] hover:underline whitespace-nowrap mt-1">
            Services due →
          </Link>
        )}
      </div>

      <div className="flex items-end gap-x-6 gap-y-2 flex-wrap border-b border-line mb-5">
        {TAB_GROUPS.map(g => (
          <div key={g}>
            <div className="px-3 text-[9.5px] uppercase tracking-wider2 text-text-dim font-mono">{g}</div>
            <div className="flex items-center">
              {TABS.filter(t => t.group === g && !HIDDEN_FROM_STRIP.has(t.key)).map(({ key, label, icon: Icon }) => (
                <button
                  key={key}
                  onClick={() => setTab(key)}
                  className={`px-3 py-2.5 text-[12px] font-medium whitespace-nowrap transition-colors inline-flex items-center gap-2 ${
                    tab === key ? "text-accent border-b-2 border-accent -mb-px" : "text-text-muted hover:text-text"
                  }`}
                >
                  <Icon size={13}/> {label}
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>

      <div className="flex items-center gap-2 mb-4 flex-wrap">
        <div className="flex items-center gap-2 px-3 py-1.5 bg-ink-raised border border-line rounded-md flex-1 max-w-md">
          <Search size={14} className="text-text-muted" />
          <input placeholder="Search…" value={search} onChange={e => setSearch(e.target.value)}
            className="bg-transparent outline-none text-text text-[13px] flex-1" />
        </div>
        {/* Vehicles stay manager+ (fleet + rego + insurance concerns). */}
        {canEdit && tab === "vehicles" && (
          <Link to="/assets/vehicles/new"><Button icon={Plus}>New vehicle</Button></Link>
        )}
        {/* Tool + rigging create is open to everyone — crew register gear
            bought on the road. RLS in migration 035 lets them INSERT;
            UPDATE/DELETE still gated to manager+. T-051 Part B. */}
        {tab === "power_tools" && (
          <Link to="/assets/power-tools/new"><Button icon={Plus}>New power tool</Button></Link>
        )}
        {tab === "hand_tools" && (
          <Link to="/assets/hand-tools/new"><Button icon={Plus}>New hand tool</Button></Link>
        )}
        {tab === "rigging" && (
          <Link to="/assets/rigging/new"><Button icon={Plus}>New rigging item</Button></Link>
        )}
      </div>

      {/* mig 351 — CapEx purchases awaiting broker registration live on their
          own finance surface; point insurance readers at it from here. */}
      {tab === "insurance_policies" && canEdit && (
        <div className="text-text-muted text-[12px] mb-3">
          Looking for significant purchases awaiting broker cover? That's the{" "}
          <Link to="/assets/insurance-register" className="text-accent hover:underline">
            Insurance register
          </Link>{" "}
          under Finance.
        </div>
      )}

      {tab === "ladders" && (
        <div className="text-text-muted text-[12px] mb-3">
          Ladders are tracked under <Link to="/hr/equipment?kind=ladder" className="text-accent hover:underline">HR → Equipment</Link>. This tab is the register editor — add or retire a ladder here.
        </div>
      )}
      {tab === "ladders"
        ? <LadderRegisterTab search={search} canEdit={canEdit} user={user as any} />
        : isEquipmentTab(tab)
        ? <EquipmentRegisterTab tab={tab} search={search} canEdit={canEdit} employees={employees} userId={(user as any)?.user_id ?? null} />
        : <AssetList tab={tab} search={search} canEdit={canEdit} employees={employees} />}
    </div>
  );
}

// ---------------------------------------------------------------------------

interface AssetListProps {
  tab:       TabKey;
  search:    string;
  canEdit:   boolean;
  employees: EmployeeOption[];
}

function AssetList({ tab, search, canEdit, employees }: AssetListProps) {
  const { data: rows = [], isLoading } = useQuery({
    queryKey: ["assets", tab],
    queryFn: async () => {
      switch (tab) {
        case "vehicles":
          return (await supabase.from("vehicles").select(`
            vehicle_id, vehicle_code, rego, make, model, year,
            rego_expiry, service_due_date, is_active,
            assigned_to_employee_id,
            assigned_to:employees!vehicles_assigned_to_employee_id_fkey(first_name, last_name)
          `).order("vehicle_code")).data ?? [];

        case "power_tools":
          return (await supabase.from("power_tools").select(`
            tool_id, tool_code, display_name, tool_category, tool_type, size_spec,
            serial_number, asset_tag_number,
            inspection_required, last_test_and_tag_date, next_test_and_tag_due,
            last_tag_number, is_active,
            assigned_to_employee_id,
            assigned_to:employees!power_tools_assigned_to_employee_id_fkey(first_name, last_name)
          `).order("tool_code")).data ?? [];

        case "hand_tools":
          return (await supabase.from("hand_tools").select(`
            tool_id, tool_code, display_name, tool_category, tool_type, size_spec,
            asset_tag, quantity, is_active,
            assigned_to_employee_id,
            assigned_to:employees!hand_tools_assigned_to_employee_id_fkey(first_name, last_name)
          `).order("tool_code")).data ?? [];

        case "rigging":
          return (await supabase.from("rigging_equipment").select(`
            rigging_id, rigging_code, display_name, equipment_class, equipment_type,
            swl_kg, destroy_by_date, status,
            kit_name, tag_colour, next_inspection_date, last_result,
            assigned_to_employee_id,
            assigned_to:employees!rigging_equipment_assigned_to_employee_id_fkey(first_name, last_name)
          `).order("rigging_code")).data ?? [];

        case "test_and_tag":
          // Full register: every T&T inspection ever done, most recent first.
          return (await supabase.from("test_and_tag").select(`
            tnt_id, tested_at, asset_tag_number, test_tag_number,
            result, visual_inspection_result, defects_found,
            action_required, next_test_due, tester_licence_number,
            power_tool:power_tools(tool_id, tool_code, display_name, serial_number),
            tester:employees!test_and_tag_tester_employee_id_fkey(first_name, last_name)
          `).order("tested_at", { ascending: false }).limit(500)).data ?? [];

        case "rigging_inspections":
          // Full register: every rigging inspection, most recent first.
          return (await supabase.from("rigging_inspections").select(`
            inspection_id, inspection_date, result, inspection_type,
            tag_colour, tag_number, condition_notes, defects_found,
            next_inspection_due,
            rigging:rigging_equipment(rigging_id, rigging_code, display_name, kit_name, serial_number),
            inspector:employees!rigging_inspections_inspector_employee_id_fkey(first_name, last_name)
          `).order("inspection_date", { ascending: false }).limit(500)).data ?? [];

        case "insurance_policies": {
          // T-404 — the legacy insurance_policies table was superseded by the
          // T-388 company compliance register (mig 227), and this query also
          // selected columns that never existed (coverage_amount/expiry_date
          // → real: cover_amount/effective_to), so the tab rendered
          // permanently empty with the error swallowed. Now reads the live
          // register, mapped to the legacy row shape so the renderers below
          // stay untouched. Manage policies at /company-compliance.
          const { data } = await supabase.from("company_compliance_items").select(`
            item_id, reference, category, provider, cover_amount, expiry_date, is_active
          `).eq("item_type", "insurance").eq("is_active", true).order("expiry_date");
          return ((data ?? []) as any[]).map(r => ({
            policy_id:       r.item_id,
            policy_number:   r.reference,
            policy_type:     r.category,
            insurer:         r.provider,
            coverage_amount: r.cover_amount,
            expiry_date:     r.expiry_date,
            is_active:       r.is_active,
          }));
        }
      }
    },
  });

  // Search blob — flatten scalar + one-level-nested values (so assigned_to.first_name
  // etc become searchable). Lets the user type an employee name to filter items
  // allocated to that person. T-057 is the longer-term global search; this is the
  // local /assets fix.
  const searchBlob = (r: any): string => {
    const parts: string[] = [];
    for (const v of Object.values(r)) {
      if (v == null) continue;
      if (typeof v === "object") {
        for (const nv of Object.values(v as Record<string, unknown>)) {
          if (nv != null && typeof nv !== "object") parts.push(String(nv));
        }
      } else {
        parts.push(String(v));
      }
    }
    return parts.join(" ").toLowerCase();
  };
  const needle = search.trim().toLowerCase();
  const filtered = (rows as any[]).filter(r => !needle || searchBlob(r).includes(needle));

  // The kit names already in use, from the UNFILTERED rows — the picker must
  // offer every kit even when the search has narrowed the table to one of them.
  // Case-insensitive sort so "Rigging Kit 4" and any stray "Rigging kit 4" land
  // next to each other instead of at opposite ends of the list.
  const kits = React.useMemo(() => {
    const set = new Set<string>();
    for (const r of rows as any[]) if (r.kit_name) set.add(String(r.kit_name));
    return Array.from(set).sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" }));
  }, [rows]);

  if (isLoading) return <div className="py-10 flex justify-center"><Spinner/></div>;
  if (filtered.length === 0) return <Card><div className="p-6"><EmptyState title="No items" /></div></Card>;

  return (
    <Card>
      <div className="overflow-x-auto">
        <table className="w-full text-[13px]">
          <thead><tr className="border-b border-line">{headersFor(tab).map(h => (
            <th key={h} className="text-left px-4 py-3 text-[10px] font-medium uppercase tracking-wider2 text-text-muted">{h}</th>
          ))}</tr></thead>
          <tbody>
            {filtered.map((r, i) => renderRow(tab, r, i, filtered.length, { canEdit, employees, kits }))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

function headersFor(tab: TabKey): string[] {
  switch (tab) {
    case "vehicles":            return ["Code", "Rego", "Vehicle", "Rego expires", "Service due", "Assigned to", "Active"];
    case "power_tools":         return ["Code", "Description", "Type", "Serial", "Asset tag", "Last T&T", "Next due", "Tag #", "Assigned to"];
    case "hand_tools":          return ["Code", "Description", "Category", "Type", "Size / spec", "Qty", "Assigned to"];
    case "rigging":             return ["Code", "Description", "Class", "Kit", "Tag", "Next insp.", "Destroy by", "Status", "Assigned to"];
    case "test_and_tag":        return ["Tested", "Tool", "Serial", "Asset tag", "Tag #", "Visual", "Result", "Next due", "Tester", "Licence"];
    case "rigging_inspections": return ["Inspected", "Equipment", "Kit", "Type", "Result", "Tag", "Tag #", "Defect", "Next due", "Inspector"];
    case "insurance_policies":  return ["Policy #", "Type", "Insurer", "Coverage", "Expires"];
  }
}

interface RenderRowCtx {
  canEdit:   boolean;
  employees: EmployeeOption[];
  /** Every kit name already in use, so the inline picker offers a LIST rather
   *  than a free-text box. James, 13 Sep: Josh Thompson showed up with two
   *  "Rigging Kit 4"s — one capital K, one lower — because kit_name is free
   *  text typed into an Input on two different screens. A list can't do that. */
  kits:      string[];
}

function renderRow(
  tab: TabKey, r: any, idx: number, total: number, ctx: RenderRowCtx,
): React.ReactNode {
  const zebra  = idx % 2 ? "bg-ink/40" : "";
  const border = idx < total - 1 ? "border-b border-line" : "";
  const rowCls = `${zebra} ${border}`;

  switch (tab) {
    case "vehicles":
      return (
        <tr key={r.vehicle_id} className={rowCls}>
          <td className="px-4 py-3 font-mono text-accent text-[12px]">
            <Link to={`/assets/vehicles/${r.vehicle_id}`} className="hover:underline">
              {r.vehicle_code}
            </Link>
          </td>
          <td className="px-4 py-3 font-mono">{r.rego}</td>
          <td className="px-4 py-3">{[r.year, r.make, r.model].filter(Boolean).join(" ")}</td>
          <td className="px-4 py-3">
            <InlineDateCell
              assetType="vehicle" assetId={r.vehicle_id} field="rego_expiry"
              value={r.rego_expiry} canEdit={ctx.canEdit}
            />
          </td>
          <td className="px-4 py-3">
            <InlineDateCell
              assetType="vehicle" assetId={r.vehicle_id} field="service_due_date"
              value={r.service_due_date} canEdit={ctx.canEdit}
            />
          </td>
          <td className="px-4 py-3">
            <AssignmentCell
              assetType="vehicle"
              assetId={r.vehicle_id}
              currentEmployeeId={r.assigned_to_employee_id ?? null}
              currentName={assignedName(r.assigned_to)}
              canEdit={ctx.canEdit}
              employees={ctx.employees}
            />
          </td>
          <td className="px-4 py-3">{r.is_active ? <Pill label="active" tone="approved" small/> : <Pill label="inactive" small/>}</td>
        </tr>
      );

    case "power_tools":
      return (
        <tr key={r.tool_id} className={rowCls}>
          <td className="px-4 py-3 font-mono text-accent text-[12px]">
            <Link to={`/assets/power-tools/${r.tool_id}`} className="hover:underline">
              {r.tool_code}
            </Link>
          </td>
          <td className="px-4 py-3">
            <div>{r.display_name}</div>
            {r.tool_category && (
              <div className="text-[11px] text-text-muted">{r.tool_category}</div>
            )}
          </td>
          <td className="px-4 py-3 text-[12px] text-text-muted">
            {r.tool_type ?? <span className="text-text-dim">—</span>}
            {r.size_spec && <span className="block text-[11px]">{r.size_spec}</span>}
          </td>
          <td className="px-4 py-3 font-mono text-[12px] text-text-muted">{r.serial_number ?? "—"}</td>
          <td className="px-4 py-3 font-mono text-[12px]">{r.asset_tag_number ?? <span className="text-text-dim">—</span>}</td>
          <td className="px-4 py-3">
            {r.inspection_required ? (
              <InlineDateCell
                assetType="power_tool" assetId={r.tool_id} field="last_test_and_tag_date"
                value={r.last_test_and_tag_date} canEdit={ctx.canEdit}
              />
            ) : <span className="text-text-dim text-[12px]">n/a</span>}
          </td>
          <td className="px-4 py-3">
            {r.inspection_required ? (
              <InlineDateCell
                assetType="power_tool" assetId={r.tool_id} field="next_test_and_tag_due"
                value={r.next_test_and_tag_due} canEdit={ctx.canEdit}
              />
            ) : <span className="text-text-dim text-[12px]">n/a</span>}
          </td>
          <td className="px-4 py-3 font-mono text-[12px]">{r.last_tag_number ?? <span className="text-text-dim">—</span>}</td>
          <td className="px-4 py-3">
            <AssignmentCell
              assetType="power_tool"
              assetId={r.tool_id}
              currentEmployeeId={r.assigned_to_employee_id ?? null}
              currentName={assignedName(r.assigned_to)}
              canEdit={ctx.canEdit}
              employees={ctx.employees}
            />
          </td>
        </tr>
      );

    case "hand_tools":
      return (
        <tr key={r.tool_id} className={rowCls}>
          <td className="px-4 py-3 font-mono text-accent text-[12px]">
            <Link to={`/assets/hand-tools/${r.tool_id}`} className="hover:underline">
              {r.tool_code}
            </Link>
          </td>
          <td className="px-4 py-3">{r.display_name}</td>
          <td className="px-4 py-3 text-[12px] text-text-muted">{r.tool_category ?? <span className="text-text-dim">—</span>}</td>
          <td className="px-4 py-3 text-[12px] text-text-muted">{r.tool_type ?? <span className="text-text-dim">—</span>}</td>
          <td className="px-4 py-3 text-[12px] text-text-muted">{r.size_spec ?? <span className="text-text-dim">—</span>}</td>
          <td className="px-4 py-3 font-mono">{r.quantity ?? 1}</td>
          <td className="px-4 py-3 text-[12px]">{assignedName(r.assigned_to) ?? <span className="text-text-dim">Pool</span>}</td>
        </tr>
      );

    case "rigging":
      return (
        <tr key={r.rigging_id} className={rowCls}>
          <td className="px-4 py-3 font-mono text-accent text-[12px]">
            <Link to={`/assets/rigging/${r.rigging_id}`} className="hover:underline">
              {r.rigging_code}
            </Link>
          </td>
          <td className="px-4 py-3">
            <div>{r.display_name}</div>
            {r.equipment_type && (
              <div className="text-[11px] text-text-muted capitalize">{r.equipment_type}</div>
            )}
          </td>
          <td className="px-4 py-3 text-[12px] text-text-muted">{r.equipment_class || <span className="text-text-dim">—</span>}</td>
          <td className="px-4 py-3 text-[12px]">
        <InlineKitCell
          assetId={r.rigging_id}
          value={r.kit_name ?? null}
          kits={ctx.kits}
          canEdit={ctx.canEdit}
        />
      </td>
          <td className="px-4 py-3"><TagColourChip value={r.tag_colour} /></td>
          <td className="px-4 py-3 text-[12px]">{fmtDate(r.next_inspection_date)}</td>
          <td className="px-4 py-3">
            <InlineDateCell
              assetType="rigging" assetId={r.rigging_id} field="destroy_by_date"
              value={r.destroy_by_date} canEdit={ctx.canEdit}
            />
          </td>
          <td className="px-4 py-3">
            <InlineStatusCell
              assetId={r.rigging_id} value={r.status} canEdit={ctx.canEdit}
            />
          </td>
          <td className="px-4 py-3">
            <AssignmentCell
              assetType="rigging"
              assetId={r.rigging_id}
              currentEmployeeId={r.assigned_to_employee_id ?? null}
              currentName={assignedName(r.assigned_to)}
              canEdit={ctx.canEdit && r.status === "in_service"}
              employees={ctx.employees}
            />
          </td>
        </tr>
      );

    case "test_and_tag": {
      const testerName = r.tester ? `${r.tester.first_name ?? ""} ${r.tester.last_name ?? ""}`.trim() : null;
      const resultLower = (r.result ?? "").toLowerCase();
      return (
        <tr key={r.tnt_id} className={rowCls}>
          <td className="px-4 py-3 text-[12px] font-mono">{fmtDate(r.tested_at)}</td>
          <td className="px-4 py-3">
            {r.power_tool ? (
              <Link to={`/assets/power-tools/${r.power_tool.tool_id}`} className="hover:underline">
                <span className="font-mono text-[12px] text-accent">{r.power_tool.tool_code}</span>
                <span className="block text-[13px] text-text">{r.power_tool.display_name}</span>
              </Link>
            ) : <span className="text-text-dim">—</span>}
          </td>
          <td className="px-4 py-3 font-mono text-[12px] text-text-muted">{r.power_tool?.serial_number ?? "—"}</td>
          <td className="px-4 py-3 font-mono text-[12px]">{r.asset_tag_number ?? <span className="text-text-dim">—</span>}</td>
          <td className="px-4 py-3 font-mono text-[12px]">{r.test_tag_number ?? <span className="text-text-dim">—</span>}</td>
          <td className="px-4 py-3 text-[12px] text-text-muted">{r.visual_inspection_result ?? "—"}</td>
          <td className="px-4 py-3">
            <Pill label={r.result ?? "—"} tone={resultLower === "pass" ? "ok" : resultLower === "fail" ? "critical" : "default"} small />
            {r.defects_found && <AlertTriangle size={11} className="inline ml-1 text-warn" />}
          </td>
          <td className="px-4 py-3"><ExpiryDate d={r.next_test_due} /></td>
          <td className="px-4 py-3 text-[12px]">{testerName ?? <span className="text-text-dim">—</span>}</td>
          <td className="px-4 py-3 text-[12px] font-mono text-text-muted">{r.tester_licence_number ?? <span className="text-text-dim">—</span>}</td>
        </tr>
      );
    }

    case "rigging_inspections": {
      const inspectorName = r.inspector ? `${r.inspector.first_name ?? ""} ${r.inspector.last_name ?? ""}`.trim() : null;
      const resultLower = (r.result ?? "").toLowerCase();
      return (
        <tr key={r.inspection_id} className={rowCls}>
          <td className="px-4 py-3 text-[12px] font-mono">{fmtDate(r.inspection_date)}</td>
          <td className="px-4 py-3">
            {r.rigging ? (
              <Link to={`/assets/rigging/${r.rigging.rigging_id}`} className="hover:underline">
                <span className="font-mono text-[12px] text-accent">{r.rigging.rigging_code}</span>
                <span className="block text-[13px] text-text">{r.rigging.display_name}</span>
                {r.rigging.serial_number && (
                  <span className="block text-[11px] text-text-muted font-mono">S/N {r.rigging.serial_number}</span>
                )}
              </Link>
            ) : <span className="text-text-dim">—</span>}
          </td>
          <td className="px-4 py-3 text-[12px]">{r.rigging?.kit_name ?? <span className="text-text-dim">Pool</span>}</td>
          <td className="px-4 py-3 text-[12px] text-text-muted">{r.inspection_type ?? "—"}</td>
          <td className="px-4 py-3">
            <Pill
              label={r.result ?? "—"}
              tone={
                resultLower === "pass" ? "ok" :
                resultLower === "restricted use" ? "warning" :
                ["fail","quarantine","destroy"].includes(resultLower) ? "critical" : "default"
              }
              small
            />
          </td>
          <td className="px-4 py-3"><TagColourChip value={r.tag_colour} /></td>
          <td className="px-4 py-3 font-mono text-[12px]">{r.tag_number ?? <span className="text-text-dim">—</span>}</td>
          <td className="px-4 py-3">
            {r.defects_found
              ? <span className="inline-flex items-center gap-1 text-[11px] text-warn"><AlertTriangle size={11}/> yes</span>
              : <span className="text-text-dim text-[12px]">—</span>}
          </td>
          <td className="px-4 py-3"><ExpiryDate d={r.next_inspection_due} /></td>
          <td className="px-4 py-3 text-[12px]">{inspectorName ?? <span className="text-text-dim">—</span>}</td>
        </tr>
      );
    }

    case "insurance_policies":
      return (
        <tr key={r.policy_id} className={rowCls}>
          <td className="px-4 py-3 font-mono text-accent">{r.policy_number}</td>
          <td className="px-4 py-3">{r.policy_type}</td>
          <td className="px-4 py-3">{r.insurer}</td>
          <td className="px-4 py-3 font-mono">{!r.coverage_amount ? "—" : isPresenting() ? MONEY_MASK : `$${Number(r.coverage_amount).toLocaleString()}`}</td>
          <td className="px-4 py-3"><ExpiryDate d={r.expiry_date} /></td>
        </tr>
      );
  }
}

// ---------------------------------------------------------------------------

function assignedName(a: { first_name: string | null; last_name: string | null } | null | undefined): string | null {
  if (!a) return null;
  const joined = `${a.first_name ?? ""} ${a.last_name ?? ""}`.trim();
  return joined || null;
}

// ---------------------------------------------------------------------------
// AssignmentCell — inline employee picker for mgr+, read-only label otherwise
// ---------------------------------------------------------------------------

interface AssignmentCellProps {
  assetType:         AssignableType;
  assetId:           string;
  currentEmployeeId: string | null;
  currentName:       string | null;
  canEdit:           boolean;
  employees:         EmployeeOption[];
}

function AssignmentCell({
  assetType, assetId, currentEmployeeId, currentName, canEdit, employees,
}: AssignmentCellProps) {
  const qc = useQueryClient();

  const mutation = useMutation({
    mutationFn: async (newEmpId: string | null) => {
      // Explicit branches per asset type. supabase-js overloads resolve the
      // Update payload + .eq() column against the concrete table each branch
      // references. A dynamic .from(tableMap[x]).eq(idColMap[x], ...) pattern
      // collapses the union to `never` which rejects every field — so we
      // spell out the three tables here. Tiny repetition, zero type drama.
      const today = new Date().toISOString().slice(0, 10);
      const payload = {
        assigned_to_employee_id: newEmpId,
        assigned_from:           newEmpId ? today : null,
      };
      let error: unknown = null;
      if (assetType === "vehicle") {
        ({ error } = await supabase
          .from("vehicles")
          .update(payload)
          .eq("vehicle_id", assetId));
      } else if (assetType === "power_tool") {
        ({ error } = await supabase
          .from("power_tools")
          .update(payload)
          .eq("tool_id", assetId));
      } else {
        ({ error } = await supabase
          .from("rigging_equipment")
          .update(payload)
          .eq("rigging_id", assetId));
      }
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["assets"] });
      qc.invalidateQueries({ queryKey: ["my_vehicles"] });
      qc.invalidateQueries({ queryKey: ["my_power_tools"] });
      qc.invalidateQueries({ queryKey: ["my_rigging"] });
    },
    onError: (err: unknown) => {
      const msg = err instanceof Error ? err.message
        : (err as { message?: string })?.message ?? "Assignment update failed";
      alert(msg);
    },
  });

  if (!canEdit) {
    return (
      <span className="text-[12px]">
        {currentName ?? <span className="text-text-dim">Pool</span>}
      </span>
    );
  }

  return (
    <select
      value={currentEmployeeId ?? ""}
      onChange={(e) => {
        const v = e.target.value;
        mutation.mutate(v === "" ? null : v);
      }}
      disabled={mutation.isPending}
      className="bg-ink-raised border border-line rounded px-2 py-1 text-[12px] text-text outline-none focus:border-accent disabled:opacity-60 max-w-[160px]"
    >
      <option value="">— Pool —</option>
      {employees.map(e => {
        const name = `${e.first_name ?? ""} ${e.last_name ?? ""}`.trim() || "(no name)";
        return <option key={e.employee_id} value={e.employee_id}>{name}</option>;
      })}
    </select>
  );
}

// ---------------------------------------------------------------------------
// Inline date cell — edit-in-place for vehicle rego/service dates, power tool
// T&T dates, rigging destroy-by. Typing / picking a date fires the update
// immediately via the per-asset-type branched mutation. Non-editors see the
// existing read-only ExpiryDate display with urgency colouring.
// T-053 quick-edit utility.
// ---------------------------------------------------------------------------

interface InlineDateCellProps {
  assetType: AssignableType;
  assetId:   string;
  field:     string;          // column name on the asset table (e.g. "rego_expiry")
  value:     string | null;
  canEdit:   boolean;
}

function InlineDateCell({ assetType, assetId, field, value, canEdit }: InlineDateCellProps) {
  const qc = useQueryClient();

  const mutation = useMutation({
    mutationFn: async (v: string | null) => {
      // Runtime-string payload; column name is parameterised so we cast the
      // payload to any — TS can't prove `field` matches an updatable column
      // without dependent types. Safety is the allow-list in each call site
      // (only columns referenced by InlineDateCell props above).
      const payload: any = { [field]: v };
      if (assetType === "vehicle") {
        const { error } = await supabase.from("vehicles").update(payload).eq("vehicle_id", assetId);
        if (error) throw error;
      } else if (assetType === "power_tool") {
        const { error } = await supabase.from("power_tools").update(payload).eq("tool_id", assetId);
        if (error) throw error;
      } else {
        const { error } = await supabase.from("rigging_equipment").update(payload).eq("rigging_id", assetId);
        if (error) throw error;
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["assets"] });
      qc.invalidateQueries({ queryKey: ["my_vehicles"] });
      qc.invalidateQueries({ queryKey: ["my_power_tools"] });
      qc.invalidateQueries({ queryKey: ["my_rigging"] });
    },
    onError: (err: unknown) => {
      const msg = err instanceof Error ? err.message : "Save failed";
      alert(msg);
    },
  });

  if (!canEdit) {
    return <ExpiryDate d={value} />;
  }

  // Urgency badge beside the input — keeps visual warning even in edit mode
  const days = value ? daysFromNow(value) : null;
  const urgency = urgencyFromDays(days);
  const showIcon = days != null && days < 30;
  const iconClass = days != null && days < 7 ? "text-danger" : "text-warn";

  return (
    <div className="flex items-center gap-1.5">
      <input
        type="date"
        defaultValue={value ?? ""}
        disabled={mutation.isPending}
        onChange={(e) => mutation.mutate(e.target.value || null)}
        className={`bg-ink-raised border rounded px-2 py-1 text-[12px] text-text outline-none focus:border-accent disabled:opacity-60 ${
          urgency === "expired" || urgency === "critical" ? "border-danger/40" :
          urgency === "warning" ? "border-warn/40" : "border-line"
        }`}
      />
      {showIcon && <AlertTriangle size={11} className={iconClass} />}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Inline status cell — rigging status (in_service / quarantined / destroyed /
// retired). Mgr+ can drive the lifecycle directly from the list.
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Inline KIT cell — move an item between kits, or out to the spare pool.
//
// James, 13 Sep: "how do we remove / change an item's kit allocation? This
// harness is not in this kit anymore, it's in pool as spare." It WAS possible,
// but only on the item's own detail page behind Edit details, and only as a
// free-text box — which is also how "Rigging Kit 4" and "Rigging kit 4" came to
// be two different kits holding the same gear.
//
// So: a picker, not a text box. The options are the kit names already in use,
// which means nobody can invent a fourth spelling of the same kit by accident.
// "New kit…" is there for a genuinely new one and prompts for the name.
// ---------------------------------------------------------------------------

interface InlineKitCellProps {
  assetId: string;
  value:   string | null;
  kits:    string[];
  canEdit: boolean;
}

function InlineKitCell({ assetId, value, kits, canEdit }: InlineKitCellProps) {
  const qc = useQueryClient();

  const mutation = useMutation({
    mutationFn: async (v: string | null) => {
      const { error } = await supabase
        .from("rigging_equipment")
        .update({ kit_name: v } as any)
        .eq("rigging_id", assetId);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["assets"] });
      qc.invalidateQueries({ queryKey: ["my_rigging"] });
      qc.invalidateQueries({ queryKey: ["inspection_register"] });
    },
    onError: (err: unknown) => {
      alert(err instanceof Error ? err.message : "Couldn't change the kit");
    },
  });

  if (!canEdit) {
    return <>{value || <span className="text-text-dim">Pool</span>}</>;
  }

  // an item whose kit isn't in the list yet (freshly typed elsewhere) still
  // shows its own value, so the picker never silently misrepresents the row
  const options = kits.includes(value ?? "") || !value ? kits : [...kits, value].sort();

  return (
    <select
      value={value ?? ""}
      disabled={mutation.isPending}
      onChange={(e) => {
        const v = e.target.value;
        if (v === "__new__") {
          const name = window.prompt("New kit name (match the spelling of the others exactly):")?.trim();
          if (name) mutation.mutate(name);
          return;
        }
        mutation.mutate(v === "" ? null : v);
      }}
      title="Which kit this item lives in. Pool = spare, not in a kit."
      className="bg-ink-raised border border-line rounded px-2 py-1 text-[12px] text-text outline-none focus:border-accent disabled:opacity-60 max-w-[150px]"
    >
      <option value="">— Pool (spare) —</option>
      {options.map((k) => <option key={k} value={k}>{k}</option>)}
      <option value="__new__">New kit…</option>
    </select>
  );
}

const RIGGING_STATUSES = ["in_service", "quarantined", "destroyed", "retired"] as const;

interface InlineStatusCellProps {
  assetId: string;
  value:   string;
  canEdit: boolean;
}

function InlineStatusCell({ assetId, value, canEdit }: InlineStatusCellProps) {
  const qc = useQueryClient();

  const mutation = useMutation({
    mutationFn: async (v: string) => {
      const { error } = await supabase
        .from("rigging_equipment")
        .update({ status: v } as any)
        .eq("rigging_id", assetId);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["assets"] });
      qc.invalidateQueries({ queryKey: ["my_rigging"] });
    },
    onError: (err: unknown) => {
      const msg = err instanceof Error ? err.message : "Status update failed";
      alert(msg);
    },
  });

  if (!canEdit) {
    return (
      <Pill
        label={value}
        tone={value === "in_service" ? "approved" : value === "quarantined" ? "warning" : "rejected"}
        small
      />
    );
  }

  return (
    <select
      value={value}
      disabled={mutation.isPending}
      onChange={(e) => mutation.mutate(e.target.value)}
      className={`bg-ink-raised border rounded px-2 py-1 text-[12px] text-text outline-none focus:border-accent disabled:opacity-60 ${
        value === "in_service"  ? "border-ok/40" :
        value === "quarantined" ? "border-warn/40" :
        "border-danger/40"
      }`}
    >
      {RIGGING_STATUSES.map(s => (
        <option key={s} value={s} className="capitalize">{s.replace(/_/g, " ")}</option>
      ))}
    </select>
  );
}

// ---------------------------------------------------------------------------

function ExpiryDate({ d }: { d: string | null }) {
  if (!d) return <span className="text-text-dim">—</span>;
  const days = daysFromNow(d);
  const u = urgencyFromDays(days);
  return (
    <div className="flex items-center gap-1.5">
      <span className={`text-[12px] font-mono ${u === "expired" || u === "critical" ? "text-danger" : u === "warning" ? "text-warn" : "text-text-muted"}`}>
        {fmtDate(d)}
      </span>
      {days != null && days < 30 && <AlertTriangle size={11} className={days < 7 ? "text-danger" : "text-warn"} />}
    </div>
  );
}

// ---------------------------------------------------------------------------
// TagColourChip — renders the quarterly rigging tag colour as a coloured pill.
// Literal colour display for now; traffic-light logic (current quarter vs
// stale) comes in the T-047 UI commit once we wire app.current_tag_colour().
// ---------------------------------------------------------------------------
function TagColourChip({ value }: { value: string | null | undefined }) {
  if (!value) return <span className="text-text-dim text-[12px]">—</span>;
  const v = value.trim();
  const key = v.toLowerCase();
  const style =
    key === "red"    ? "bg-danger/15 text-danger border-danger/40" :
    key === "green"  ? "bg-ok/15 text-ok border-ok/40" :
    key === "blue"   ? "bg-info/15 text-info border-info/40" :
    key === "yellow" ? "bg-warn/15 text-warn border-warn/40" :
                       "bg-text/8 text-text-muted border-line";
  return (
    <span className={`inline-flex items-center gap-1 rounded border px-1.5 py-0.5 text-[10px] font-mono uppercase tracking-wider2 ${style}`}>
      <span className={`inline-block w-2 h-2 rounded-full ${
        key === "red"    ? "bg-danger" :
        key === "green"  ? "bg-ok" :
        key === "blue"   ? "bg-info" :
        key === "yellow" ? "bg-warn" :
                           "bg-text-muted"
      }`} />
      {v}
    </span>
  );
}
